<template>
    <li class="flex items-start gap-2 border-b p-3 sm:items-center">
        <div class="flex flex-col self-start">
            <div class="hidden pl-4 md:block">
                <prime-avatar
                    :icon="icon"
                    size="large"
                    shape="circle"
                    class="!bg-gray-200 text-gray-800"
                />
            </div>
            <div class="pl-4 sm:pr-1 md:hidden md:pr-0">
                <prime-avatar
                    :icon="icon"
                    size="normal"
                    shape="circle"
                    class="!bg-gray-200 text-gray-800"
                />
            </div>
        </div>

        <div class="flex flex-1 flex-col lg:mr-16">
            <div
                class="flex flex-col items-start gap-1 lg:flex-row lg:items-center lg:gap-4"
            >
                <div class="text-xl font-medium text-gray-900">
                    {{
                        getPlanTitle(
                            contractData.planPresentation?.title,
                            contractData.plan.name
                        )
                    }}
                </div>
                <template v-if="contractLink !== null">
                    <div class="hidden text-gray-500 lg:block">&mdash;</div>
                    <nuxt-link :to="contractLink.link">
                        <prime-button
                            link
                            :label="contractLink.label"
                            class="!p-0"
                        />
                    </nuxt-link>
                </template>
            </div>
            <div class="flex flex-wrap items-center gap-2 !text-xs">
                <contract-status-badge
                    v-for="(badge, index) in contract.statusBadges"
                    :key="index"
                    :badge="badge"
                />
            </div>
        </div>

        <div class="text-lg font-medium text-gray-900">
            {{ formattedPrice }}
        </div>
    </li>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const ContractLineItemDisplayPlanContractFragment =
    graphql(/* GraphQL */ `
        fragment ContractLineItemDisplayPlanContractFragment on PlanContract {
            id
            planType
            plan {
                name
            }
            planPresentation(language: ENGLISH) {
                title
            }
            statusBadges
            startDateLocal
            endDateLocal
            friendlyPlanType
        }
    `)
</script>

<script setup lang="ts">
import ContractStatusBadge from '~/components/ContractDetail/ContractStatusBadge.vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import PrimeButton from 'primevue/button'
import { useRouteLink } from '~/composables/routing'
import nuxtLink from '#app/components/nuxt-link'
import { computed } from 'vue'
import PrimeAvatar from 'primevue/avatar'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { Decimal } from '~/composables/support/decimal'

const props = defineProps<{
    contract: FragmentType<typeof ContractLineItemDisplayPlanContractFragment>
}>()

const contractData = computed(() =>
    getFragmentData(ContractLineItemDisplayPlanContractFragment, props.contract)
)

const { isKiosk } = useLayoutMode()
const { profileBookingLink, profileMembershipLink, profilePassLink } =
    useRouteLink()
const contractLink = computed(() => {
    if (!isKiosk.value) {
        if (contractData.value.planType === 'MEMBERSHIP') {
            return {
                label: 'View Membership',
                link: profileMembershipLink(contractData.value.id)
            }
        } else if (contractData.value.planType === 'PROGRAM') {
            return {
                label: 'View Booking',
                link: profileBookingLink(contractData.value.id)
            }
        } else if (contractData.value.planType === 'PASS') {
            return {
                label: 'View Pass',
                link: profilePassLink(contractData.value.id)
            }
        }
    }
    return null
})

const icon = computed(() => {
    if (contractData.value.planType === 'PASS') {
        return 'pi pi-ticket'
    }

    if (contractData.value.planType === 'PROGRAM') {
        return 'pi pi-calendar'
    }

    return 'pi pi-users'
})

const formattedPrice = computed(() => {
    return Decimal.zero().formatCurrency()
})

function getPlanTitle(presentation: string | undefined, planName: string) {
    if (presentation === undefined || presentation === '') {
        return planName
    }

    return presentation
}
</script>
